import _ from "lodash";
import { useState } from "react";

import { useAppContext } from "../../../context";
import useLoading from "../../../customHooks/useLoading";
import { IFHL, IFWB, IPayment } from "../../../globals/interfaces";
import { getDate, validateSchema, notify } from "../../../utils";
import { DeletePayment } from './interfaces';
import { deletePaymentSchema } from './utils';
import apiClient from "../../../apiClient";

export default function useData () {

    const { user } = useAppContext();
    const { setLoading } = useLoading();
    const [paymentsData, setPaymentsData] = useState<Array<IPayment>>([]);
    const [fwb, setFwb] = useState<IFWB>();
    const [fhl, setFhl] = useState<IFHL>();
    const [searchComplete, setSearchComplete] = useState(false);

    const searchPayments = async (s_awb: string, s_hawb: string, s_unit: string) => {
        setLoading(true);
        const data = {
            s_awb,
            s_hawb,
            s_unit: s_unit
        };

        const res = await apiClient.get(`/payment?s_awb=${s_awb}&s_hawb=${s_hawb}&s_unit=${s_unit}`, { data });

        if (res.status === 200) {
            const { main = [], fwb = undefined, fhl = undefined } = res.data;
            if (main.length === 0) {
                notify('No records found', 'warn');
            }

            setPaymentsData(main);
            setFwb(fwb);
            setFhl(fhl);
            setSearchComplete(true);
        }
        setLoading(false);
    };

    const createPayment = async (data: any): Promise<boolean> => {
        const OVERRIDE = data.s_payment_method === 'OVERRIDE' ;
        const STORAGE_CHARGE_CREATED = data.s_payment_method === 'CHARGE' && data.s_payment_type === 'STORAGE';
        if ((OVERRIDE && data.f_amount > 1000) || STORAGE_CHARGE_CREATED) {
            data.s_status = 'PENDING';
            data.b_override_approved = 0;
        } else {
            // if not OVERRIDE above 1000 or NOT storage, don't need approval
            data.s_status = 'ACTIVE';
            data.b_override_approved = 1;
        }
        if (data.s_payment_method === 'CHARGE') {
            data.f_amount *= -1;
        }

        data.OVERRIDE = OVERRIDE;
        data.STORAGE_CHARGE_CREATED = STORAGE_CHARGE_CREATED;

        if (data.storageAmount) {
            data.s_notes = data.s_notes + ` System total: ${data.storageAmount}`;
        }

        const res = await apiClient.post('/payment', data);
        if (res.status === 200) {
            setPaymentsData(prev => {
                const copy = _.cloneDeep(prev);
                copy.push(...res.data);
                return copy;
            });
        }
        return res.status === 200;
    }

    const deletePayment: DeletePayment = async (selectedPayment: IPayment) => {
        const { i_id, s_awb, s_hawb, f_amount, s_payment_type, s_payment_method } = selectedPayment;
        const data: any = {
            i_id,
            s_awb,
            s_hawb,
            f_amount,
            s_payment_type,
            s_payment_method,
            s_notes: null,
            s_created_by: user.s_email,
            s_modified_by: user.s_email,
            t_modified: getDate(),
            s_unit: user.s_unit,
            other: {
                username: user.displayName,
                fwb,
                fhl
            }
        }
        const validSchema = await validateSchema(deletePaymentSchema, data);
        if (validSchema) {
            const res = await apiClient.post('/delete-payment', data);
            if (res.status === 204) {
                setPaymentsData(prev => {
                    const filtered = prev.filter(payment => payment.i_id !== data.i_id);
                    return filtered;
                });
            }
            return res.status === 204;
        }
        return false;
    }

    const searchMissingPayment = async (s_mawb: string) => {
        setLoading(true);
        const res = await apiClient.get(`/payment/missing?s_mawb=${s_mawb}&s_email=${user.s_email}`);
        setLoading(false);
        if (res.status === 200) {
            if (res.data.length > 0) {
                setPaymentsData(prev => {
                    const combined = [...prev,...res.data];
                    return combined;
                });
            } else {
                notify('No additional payments found', 'warn');
            }
        }
    }

    return {
        paymentsData,
        fwb,
        fhl,
        searchComplete,
        setSearchComplete,
        searchPayments,
        createPayment,
        deletePayment,
        searchMissingPayment
    }

}